import LoadingButton from "@mui/lab/LoadingButton";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import { isEmptyObject } from "../../../helpers/methods";
import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useForm } from "../../../hooks/useForm";
import useFooService from "../../../services/fooService";
import BasicDialog from "../../base/BasicDialog";
import ExampleForm from "../../form/ExampleForm";
import LoaderWrapper from "../../wrapper/LoaderWrapper";

function ExampleDialog(props) {
    const { t } = useTranslation();
    const snackbarAlert = useSnackbarAlert();

    const {
        formValue,
        setFormValue,
        onChange,
        onChangeDate,
        onChangeAutocompleteFieldWithObjectOptions,
    } = useForm({});

    const { createFoo, updateFoo, getFooDetails } = useFooService();

    const updateFooFn = useAsyncFn(updateFoo);
    const createFooFn = useAsyncFn(createFoo);

    const fooDetailsData = useAsync(() => {
        if (props.fooId) {
            return getFooDetails(props.fooId).catch((error) => {
                console.error("object not found");
            });
        }
        return Promise.resolve(formValue);
    }, [props.fooId]);

    const fooFilteringData = useAsync(() => {
        return Promise.resolve([]);
    }, []);

    const prepareData = useCallback((data) => {
        // some data preparations
        return data;
    }, []);

    useEffect(() => {
        if (fooDetailsData.loading) {
            return;
        }
        if (fooDetailsData.value !== undefined && !isEmptyObject(fooDetailsData.value)) {
            setFormValue(fooDetailsData.value);
        }
    }, [fooDetailsData.loading]);

    const onSubmit = useCallback(() => {
        if (props.fooId) {
            updateFooFn
                .execute(props.fooId, prepareData(formValue))
                .then((res) => {
                    snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.foo_updated"));
                    props.onClose();
                    props.onRefetch();
                })
                .catch((error) => {
                    snackbarAlert.openErrorSnackbarAlert(
                        t("snackbar_alert.occurred_error_during_foo_updating"),
                    );
                });
        } else {
            createFooFn
                .execute(prepareData(formValue))
                .then((res) => {
                    snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.foo_created"));
                    props.onClose();
                    props.onRefetch();
                })
                .catch((error) => {
                    snackbarAlert.openErrorSnackbarAlert(
                        t("snackbar_alert.occurred_error_during_foo_creating"),
                    );
                });
        }
    }, [formValue]);

    const isValid = true; // check is valid

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign="center"
            contentAlign="center"
            title={
                props.fooId
                    ? t("dialog.foo_details_dialog.foo_details")
                    : t("dialog.foo_details_dialog.add_foo")
            }
            maxWidth="sm"
            showDialogActions
        >
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                rowGap={1}
            >
                {fooDetailsData.loading || fooFilteringData.loading ? (
                    <LoaderWrapper isLoading={true} />
                ) : (
                    <>
                        <Grid item xs={12}>
                            <ExampleForm
                                data={formValue}
                                onChange={onChange}
                                onChangeAutocomplete={onChangeAutocompleteFieldWithObjectOptions}
                                onChangeDate={onChangeDate}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                fullWidth
                                loading={updateFooFn.loading}
                                disabled={!isValid}
                                onClick={onSubmit}
                            >
                                {props.fooId ? t("save") : t("create")}
                            </LoadingButton>
                        </Grid>
                    </>
                )}
            </Grid>
        </BasicDialog>
    );
}

ExampleDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onRefetchData: PropTypes.func,
    fooId: PropTypes.string,
    readOnly: PropTypes.bool,
};

ExampleDialog.defaultProps = {
    open: false,
    readOnly: false,
};

export default ExampleDialog;
